/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { ErrorResponseModel } from '../../../../../../core/models';
import { GslbSubmissionService } from './gslb.wizard.submission.service';
import { Injectable } from '@angular/core';
import { Layer47SubmissionService } from './layer4-7.wizard.submission.service';
import { LbErrorService } from './lb.error.service';
import {
    LoadingService,
    LogService,
    NavigationService,
    NotifyService,
    TransactionService,
    UtilsService
} from '../../../../../../core/services';
import { LockApiService } from '../../../http';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { TemplateModel } from '../../../models';
import { WizardService } from './wizard.service';
import { WizardStepModel } from '../../../models/wizardStep.model';
import { WizardTitleEnum } from '../../../enums';
import { concatMap } from 'rxjs/operators';

@Injectable()
export class WizardSubmissionService {
    /**
     * Emit our Transaction state.
     */
    public transactionState: Subject<any> = new Subject<any>();

    /**
     * State of transaction mode.
     * @private
     */
    private _transactionActive: boolean = false;

    /**
     * Keep track of submission attempts.
     * @private
     */
    private submissionAttempts: number = 1;

    /**
     * Store submission subscribers.
     * @private
     */
    private submissionSubscribers: any = {};

    /**
     * Store the error ID so the correct template content is loaded in the error state
     * @private
     */
    private storedErrorId: string = null;

    /**
     * Store the error index so the correct template content is loaded in the error state
     * @private
     */
    private storedErrorIndex: number = null;

    constructor(
        private gslbSubmissionService: GslbSubmissionService,
        private layer47SubmissionService: Layer47SubmissionService,
        private lbErrorService: LbErrorService,
        private loadingService: LoadingService,
        private lockApiService: LockApiService,
        private logService: LogService,
        private navigationService: NavigationService,
        private notifyService: NotifyService,
        private router: Router,
        private transactionService: TransactionService,
        private utilsService: UtilsService,
        private wizardService: WizardService
    ) {
    }

    /**
     * Set transaction active state
     * @param state
     */
    set transactionActive(state: boolean) {
        this._transactionActive = state;
        this.transactionState.next(this._transactionActive);
    }

    /**
     * Check to see if there is an active transaction on this box.
     */
    public checkTransactionStatus(): void {
        this.lockApiService.getLockStatus()
            .subscribe({
                next: (response: any): void => {
                    if (response.TransactionLocked) {
                        this.transactionActive = true;
                        const callBack = this.rollBackCallBackHelper();
                        this.notifyService.error('The system is currently locked by a transaction.<br>' +
                            'Only force rollback if you are sure it is not in use by another user.', {
                            id: 'lock-notify',
                            static: true,
                            callBack
                        });
                    } else {
                        this.transactionActive = false;
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    const htmlDebugMessage = this.utilsService.formatCapturedError(error);
                    this.notifyService.error(htmlDebugMessage, { static: true });
                }
            });
    }

    /**
     * Helper adds call back function to notification.
     */
    public rollBackCallBackHelper(): any {
        return {
            text: 'force_rollback',
            function: this.forceRollback.bind(this)
        };
    }

    /**
     * Debug function to force a rollback if the system is already in transaction mode.
     */
    public forceRollback(): void {
        this.transactionService.rollbackTransaction()
            .subscribe({
                next: (): void => {
                    // On success remove the debug notification.
                    this.notifyService.removeNotificationById('lock-notify');
                    this.transactionActive = false;
                    this.wizardService.controllerState[WizardTitleEnum.SUCCESS] = [];
                },
                error: (error: ErrorResponseModel): void => {
                    const htmlDebugMessage = this.utilsService.formatCapturedError(error);
                    this.notifyService.error(htmlDebugMessage, { static: true });
                }
            })
            .add((): void => {
                this.loadingService.loadingButtonEmitter.next(false);
            });
    }

    /**
     * Start transaction helper function.
     * @param controllerState
     * @param navigationUrl
     */
    public startTransaction(controllerState: any, navigationUrl: string): void {
        this.transactionService.startTransaction()
            .pipe(this.concatMapHelper(controllerState))
            .subscribe({
                next: (): void => {
                    this.commitTransaction(navigationUrl);
                },
                error: (error: ErrorResponseModel): void => {
                    this.notifyService.unmuteLocalNotifications();
                    this.submitFailed(error);
                }
            });
    }

    /**
     * Concat helper
     * @param controllerState
     * @private
     */
    private concatMapHelper(controllerState: any): any {
        const template: TemplateModel = this.wizardService.selectedTemplate;
        const clonedControllerState: any = this.utilsService.duplicateObject(controllerState);

        switch (template.type) {
            case 'layer4':
            case 'layer7':
            case 'monitor':
                this.layer47SubmissionService.setApiServiceOnTemplateType(template.type);

                return this.layer47SubmissionService.concatMapHelper(clonedControllerState);
            case 'gslb':
                this.gslbSubmissionService.setApiService();

                return this.gslbSubmissionService.concatMapHelper(clonedControllerState);
        }
    }

    /**
     * Commit transaction.
     * @param navigationUrl
     */
    public commitTransaction(navigationUrl: string): void {
        this.wizardService.wizardProgressMessage = 'Just adding the finishing touches';

        // Commit our transaction
        this.transactionService.commitTransaction()
            .subscribe({
                next: (): void => {
                    this.notifyService.unmuteLocalNotifications();
                    this.logService.debug('WizardSubmissionService:commitTransaction()', 'Wizard is complete');
                    this.notifyService.success(
                        'Your new deployment has been created as configured.',
                        { id: 'wizardComplete' });

                    this.router.navigateByUrl(this.navigationService.getEnduranceUrl(navigationUrl)).then(
                        (): void => {
                            this.wizardService.resetWizardService();
                            this.wizardService.clearSubmissionClassVariables();
                        }
                    ).catch();
                    this.loadingService.loadingButtonEmitter.next(false);
                },
                error: (error: ErrorResponseModel): void => {
                    this.wizardService.clearSubmissionClassVariables();
                    this.submitFailed(error);
                }
            });
    }

    /**
     * Submit helper
     * @param redirectUrl
     */
    public submit(redirectUrl: string): void {
        this.logService.info('WizardSubmissionService:submit()', 'WizardService ControllerState', this.wizardService.controllerState);
        this.wizardService.submissionActive = true;

        //Check if the controller state had errors before clearing them.
        const hadErrors: boolean = !!this.utilsService.getObjectValue(this.wizardService.controllerState, 'errorState');

        // Make sure the error state object is clear.
        this.storedErrorId = null;
        this.storedErrorIndex = null;
        this.wizardService.clearOutstandingStates();
        this.lbErrorService.errorsLoaded = false;
        this.notifyService.removeNotificationById('wizardError');
        const selectedTemplate = this.wizardService.selectedTemplate;

        // Check if the wizardMode is partner and the template has builds,
        // if so we should use the builds structure for creating the new service.
        this.wizardService.controllerState = this.getControllerStateForTemplateType(selectedTemplate, hadErrors);
        this.notifyService.muteLocalNotifications();
        this.checkTransactionStatus();

        this.submissionAttempts = 1;

        // Before submit check transaction status.
        const transactionSubscriber = this.transactionState
            .subscribe(state => {
                if (!state) {
                    this.wizardService.wizardProgressMessage = 'Getting ready..';
                    this.startTransaction(this.wizardService.controllerState, redirectUrl);
                    // Unsubscribe straight away in case the wizard fails and submit is called again.
                    transactionSubscriber.unsubscribe();
                } else {
                    this.transactionMessaging();
                }
            });

        this.submissionSubscribers['transactionSubscriber'] = transactionSubscriber;
    }

    /**
     * Get controller state for template type.
     * @param selectedTemplate
     * @param hadErrors
     * @private
     */
    private getControllerStateForTemplateType(selectedTemplate: TemplateModel, hadErrors: boolean): any {
        return this.wizardService.wizardMode === 'partner' && selectedTemplate.hasOwnProperty('builds') && !hadErrors
            ? this.getSectionsData(selectedTemplate)
            : this.wizardService.controllerState;
    }

    /**
     * If a template uses builds get the section data to create a new controller state.
     * @param selectedTemplate
     * @private
     */
    private getSectionsData(selectedTemplate: TemplateModel): any {
        const storedUserValues = this.getUserValuesFromWizardControllerState(selectedTemplate, this.wizardService.controllerState);
        // Loop through the build section of the template.
        const newControllerState: any = {};

        // Go through each service in the sections section!
        for (const serviceArray in selectedTemplate.sections) {
            if (selectedTemplate.sections.hasOwnProperty(serviceArray) && serviceArray !== 'fipsReference') {
                const sectionData = selectedTemplate.sections[serviceArray];
                const tempServices: any[] = [];
                if (!this.wizardService.controllerState[serviceArray]) {
                    this.logService.error(
                        'WizardSubmissionService: getSectionsData',
                        'There is a mismatch between the sections and builds in the template'
                    );
                    newControllerState['error'] = [serviceArray];

                    return newControllerState;
                }

                // Loop through each 'frontend, backend and real server' from the template sections.
                for (const service of sectionData) {
                    const serviceFormData: any = this.utilsService.duplicateObject(
                        this.wizardService.controllerState[serviceArray])[0];
                    const mergedServiceData: any = this.mergeServicePropertyData(service, serviceFormData, storedUserValues);
                    tempServices.push(mergedServiceData);
                }
                newControllerState[serviceArray] = tempServices;
            }
        }

        return newControllerState;
    }

    /**
     * Merge service property data from form and template.
     * @param service
     * @param serviceFormData
     * @param storedUserValues
     * @private
     */
    private mergeServicePropertyData(service: any, serviceFormData: any, storedUserValues: any): any {
        const newService = {};
        // Loop through each property in the service.
        for (const property in service) {
            if (service.hasOwnProperty(property) && property !== 'title') {
                const formValue = this.utilsService.getObjectValue(serviceFormData, `${property}.value`);
                const buildDataValue = this.utilsService.getObjectValue(service, `${property}.value`);
                const storedValue: string = this.getPropertyFromStoredConfig(storedUserValues, property);

                if (formValue === undefined && buildDataValue === undefined) {
                    newService[property] = buildDataValue;
                    this.logService.error(
                        'WizardSubmissionService: mergeServicePropertyData',
                        'There is missing data for property: ' + property);
                } else {
                    if (property !== 'backendId') {
                        newService[property] = { value: this.getPropertyValue(storedValue, buildDataValue, formValue) };
                    }
                }
            }
        }

        return Object.assign(serviceFormData, newService);
    }

    /**
     * Try and find the value in other service values.
     * @param storedUserValues
     * @param property
     * @private
     */
    private getPropertyFromStoredConfig(storedUserValues: any, property: string): string {
        for (const service in storedUserValues) {
            if (storedUserValues.hasOwnProperty(service)) {
                const matchedProperty = storedUserValues[service].find((item: any) => item.field === property);
                if (matchedProperty?.value) {
                    return matchedProperty.value;
                }
            }
        }
    }

    /**
     * Get property value.
     * @param storedUserValue
     * @param buildDataValue
     * @param formValue
     * @private
     */
    private getPropertyValue(storedUserValue: any, buildDataValue: string, formValue: string): any {
        let value: string;
        if (typeof (buildDataValue) === 'string' && buildDataValue.indexOf('[placeHolder]') !== -1) {
            // Get a value from formValue, storedUserValue or send an empty string!
            const newValue = formValue ? formValue : storedUserValue ? storedUserValue : '';
            value = buildDataValue.replace('[placeHolder]', newValue);
        } else if (buildDataValue === '[placeHolder]') {
            value = formValue;
        } else {
            value = buildDataValue;
        }

        return value;
    }

    /**
     * Setup transaction messaging.
     * @private
     */
    private transactionMessaging(): void {
        let setTimeoutId: any;
        let setTimeout2Id: any;
        let setIntervalId: any;

        if (this.submissionAttempts > 4) {
            this.cleanUpSubmissionProgress();
        } else {
            let counter = 5;
            // Set initial message.
            this.wizardService.wizardProgressMessage = this.setTransactionProgressMessage(counter);

            // Message counter
            setIntervalId = setInterval(() => {
                counter--;
                // Set counter message.
                this.wizardService.wizardProgressMessage = this.setTransactionProgressMessage(counter);
                if (counter === 0) {
                    clearInterval(setIntervalId);
                }
            }, 1000);

            // Functional timer
            setTimeoutId = setTimeout(() => {
                this.wizardService.wizardProgressMessage = 'Trying again';
                setTimeout2Id = setTimeout(() => {
                    this.checkTransactionStatus();
                    this.submissionAttempts++;
                    clearTimeout(setTimeout2Id);
                }, 1500);
                clearTimeout(setTimeoutId);
            }, 5000);
        }

        // Store timer ids for removal later.
        this.submissionSubscribers['setTimeoutId'] = setTimeoutId;
        this.submissionSubscribers['setTimeout2Id'] = setTimeout2Id;
        this.submissionSubscribers['setIntervalId'] = setIntervalId;
    }

    /**
     * Set the transaction progress message.
     * @param counter
     * @private
     */
    private setTransactionProgressMessage(counter: number) {
        return this.transactionProgressMessage(counter, this.submissionAttempts);
    }

    /**
     * Format the transaction progress message.
     * @param counter
     * @param attempts
     */
    public transactionProgressMessage(counter: any, attempts: number): string {
        return `Transaction locked by another user,<br> trying again in ${counter} seconds<br> attempt ${attempts} of 4`;
    }

    /**
     * Clean up progress messaging.
     * @private
     */
    private cleanUpSubmissionProgress(): void {
        const { setTimeoutId, setTimeout2Id, setIntervalId, transactionSubscriber } = this.submissionSubscribers;

        // Clean everything up
        this.wizardService.submissionActive = false;
        this.loadingService.loadingButtonEmitter.next(false);
        this.submissionAttempts = 1;
        clearTimeout(setTimeoutId);
        clearTimeout(setTimeout2Id);
        clearInterval(setIntervalId);

        // Unsubscribe after max attempts as wizard might be submitted is called again.
        transactionSubscriber.unsubscribe();
    }

    /**
     * Submit Failed.
     * @param errorObject
     */
    public submitFailed(errorObject: any): void {
        this.wizardService.wizardProgressMessage = 'Loading Errors...';
        this.logService.error('WizardSubmissionService:submitFailed()', 'There was an error submitting the wizard', errorObject);

        this.lockApiService.pollForLockStatus()
            .pipe(concatMap(() => this.transactionService.rollbackTransaction()))
            .subscribe({
                next: (): void => {
                    delete this.wizardService.controllerState[WizardTitleEnum.SUCCESS];
                    this.navigateToParentStepThatFailed(errorObject);
                },
                error: (error: ErrorResponseModel): void => {
                    this.notifyService.unmuteLocalNotifications();
                    const htmlDebugMessage = this.utilsService.formatCapturedError(error);
                    this.notifyService.error(htmlDebugMessage, { static: true });
                    this.logService.error('WizardSubmissionService:submitFailed()', 'Failed to get lock status', error);
                }
            });
    }

    /**
     * Find the section in the wizard that errored and navigates to it.
     * @param errorObject
     */
    public navigateToParentStepThatFailed(errorObject: ErrorResponseModel): void {
        this.storedErrorId = errorObject.extras.storedErrorId;
        this.storedErrorIndex = errorObject.extras.storedErrorIndex;
        // Delay the closing of the submission modal.
        const submissionModalTimer: any = window.setTimeout(() => {
            this.loadingService.loadingButtonEmitter.next(false);
            this.wizardService.clearSubmissionClassVariables();
            window.clearTimeout(submissionModalTimer);
        }, 5000);

        const { sectionName, index } = this.getNavigationDetails(errorObject);
        const errorStep = this.wizardService.wizardSteps[index];
        if (errorStep && !errorStep.isVisible) {
            errorStep.isVisible = true;
            this.logService.warn('WizardSubmissionService:navigateToParentStepThatFailed', 'Forced error step to be visible');
        }

        const message: string = this.getNotificationMessage(errorObject);
        this.notifyService.unmuteLocalNotifications();

        if (!sectionName || index < 0) {
            this.notifyService.error(message, {
                id: 'wizardError',
                wizardError: 'wizardError',
                errorId: errorObject['uId']
            });
        }

        if (sectionName && index !== -1) {
            // Store the error in the controller state, to be picked up in the component with error.
            this.utilsService.createArrayIfNotExist(this.wizardService.controllerState, 'errorState');
            this.wizardService.controllerState['errorState'] = {
                [sectionName]: {
                    error: errorObject.error.errors,
                    index: this.storedErrorIndex,
                    id: this.storedErrorId
                }
            };

            this.wizardService.goToStep(index, null, true);
        }
    }

    /**
     * Get message and navigation variables for notification and navigation.
     * @param errorObject
     * @private
     */
    private getNavigationDetails(errorObject: ErrorResponseModel): any {
        let sectionName: string = this.getSectionNameFromError(errorObject);
        // Check if the selected template is a monitor, if so change the section to frontend to validate the rules.
        if (this.wizardService.selectedTemplate.type === 'monitor' &&
            (sectionName === WizardTitleEnum.FRONTENDS || sectionName === WizardTitleEnum.ACLS)
        ) {
            sectionName = WizardTitleEnum.MONITORS;
        }

        const index: number = this.getServiceIndexWithStoreErrorIndex(this.storedErrorIndex, sectionName);

        return { sectionName, index };
    }

    /**
     * Get service index considering the storedErrorIndex.
     * @param storedErrorIndex
     * @param sectionName
     * @private
     */
    private getServiceIndexWithStoreErrorIndex(storedErrorIndex: number, sectionName: string): number {
        const services: any[] = this.wizardService.wizardSteps.filter(item => item.service === sectionName);
        const navigationIndex: number = storedErrorIndex > (services.length - 1) ? 0 : storedErrorIndex;

        return this.wizardService.wizardSteps.findIndex(
            (item: any): boolean => item.service === sectionName && item.serviceIndex === navigationIndex
        );
    }

    /**
     * Get notification message.
     * @param errorObject
     * @private
     */
    private getNotificationMessage(errorObject: ErrorResponseModel): string {
        const errorType: any = this.utilsService.getObjectValue(errorObject, 'error.errors[0].type');
        const errorField: any = this.utilsService.getObjectValue(errorObject, 'error.errors[0].field');
        const errorMessage: string = this.utilsService.getObjectValue(errorObject, 'error.errors[0].message');

        return errorType && errorField && errorMessage
            ? `${this.formatErrorType(errorType)} ${errorField} - ${errorMessage}`
            : `The wizard was unable to configure the deployment specified. Please fix the error. ${errorObject.message}`;
    }

    /**
     * Format error type.
     * @param errorType
     * @private
     */
    private formatErrorType(errorType: string): string {
        return this.utilsService.firstLetterUppercase(errorType).replace('_', ' ');
    }

    /**
     * Get section name from error.
     * @param errorObject
     * @private
     */
    public getSectionNameFromError(errorObject: any): string | null {
        const errorSection: any = this.utilsService.getObjectValue(errorObject, 'error.model');
        if (!errorSection) {
            this.logService.debug('WizardSubmissionService::getSectionNameFromError', 'No error model found.');

            return null;
        }

        const referenceParts: any = errorSection.split(':');
        if (!referenceParts[1]) {
            this.logService.debug('WizardSubmissionService::getSectionNameFromError', 'errorSection couldn\'t be split.');
        } else {
            switch (referenceParts[1]) {
                case 'Frontend':
                case 'Frontends':
                case 'Cert':
                    return WizardTitleEnum.FRONTENDS;
                case 'Backend':
                case 'Backends':
                case 'HealthCheck':
                    return WizardTitleEnum.BACKENDS;
                case 'RealServer':
                case 'RealServers':
                    return WizardTitleEnum.REALSERVERS;
                case 'ACL':
                case 'ACLs':
                    return WizardTitleEnum.ACLS;
                default:
                    this.logService.debug(
                        'WizardSubmissionService::getSectionNameFromError', 'No switch cases matching ', referenceParts[1]
                    );

                    return null;
            }
        }
    }

    /**
     * Get values already inputted by the user.
     * @param selectedTemplate
     * @param controllerState
     */
    public getUserValuesFromWizardControllerState(selectedTemplate: TemplateModel, controllerState: any): any {
        const storedValues = {};

        // Get partner values.
        const stepsWithValues: any[] = this.wizardService.wizardSteps.filter(
            step => (step.isActive || step.isComplete) && step.id !== 'templateSelection'
        );

        for (const step of stepsWithValues) {
            const buildTemplateFields: any = selectedTemplate.builds[step.service][step.serviceIndex];
            const sectionTemplateFields: any = selectedTemplate.sections[step.service][step.serviceIndex];
            storedValues[step.service] = this.getValuesFromFields(controllerState, step, buildTemplateFields, sectionTemplateFields);
        }

        return storedValues;
    }

    /**
     * Get values from the fields in the controller state.
     * @param controllerState
     * @param step
     * @param templateFields
     * @param sectionTemplateFields
     * @private
     */
    private getValuesFromFields(controllerState: any, step: WizardStepModel, templateFields: any, sectionTemplateFields: any): any[] {
        const storedValues: any[] = [];
        for (const field in templateFields) {
            if (templateFields.hasOwnProperty(field)) {
                const controllerValue: any = controllerState[step.service][step.serviceIndex][field]?.value;

                if (controllerValue !== undefined || controllerValue !== null) {
                    const prefix: string = this.getPrefix(sectionTemplateFields, field);
                    const newValue: any = prefix && controllerValue.includes(prefix)
                        ? controllerValue.replace(prefix, '') : controllerValue;
                    storedValues.push({ 'field': field, 'value': newValue });
                }
            }
        }

        return storedValues;
    }

    /**
     * Get prefix.
     * @param sectionTemplateFields
     * @param field
     * @private
     */
    private getPrefix(sectionTemplateFields: any, field: string): string {
        return sectionTemplateFields[field]?.value?.includes('[placeHolder]')
            ? sectionTemplateFields[field].value.replace('[placeHolder]', '')
            : null;
    }
}
